import { DeepKeys } from 'utils/type-tools';
import { kitTexts } from 'i18n/kit.texts';

const customization = {
  /**
   * Exposed project specific variables like logos, email addresses, etc.
   */
  variables: {
    pdf: {
      logo: '/@greenline/Hose.com_BLUE.jpg',
    },
    contactEmail: process.env.REACT_APP_CONTACT_EMAIL,
    contactEmailTitle: 'Custom Assembly Configuration',
    fittingNoImage: '',
    // iframe event @cpq group for postMessage payloads.
    // Intentionally omitted: singleICPQCartEvent, summaryICPQCartEvent,
    // summaryICPQCartEventSeparated. i18next deepExtend cannot overwrite
    // non-string primitive leaves, so boolean flags must come only from
    // the server override (read via readBool() helper in SummaryView).
    singleICPQCartGroup: '@cpq',
    summaryICPQCartGroup: '@cpq',
  },
  /**
   * CSS variable overrides
   * @see /src/styles/css-variables.scss
   */
  css: {
    'color-accent': '#00559d',
    'header-color-code': '#444',
    'progress-bar-color': '#444',
    'font-family-header': '"Exo 2", sans-serif',
    'subheader-color-code': '#008752',
  },
  texts: {
    kits: kitTexts,
    step1: {
      title: 'Select Hose',
      tabName: 'Select Hose',
      description: 'Please use the below drop-down options to select the hose specifications you are looking for.',
    },
    step2: {
      title: 'Select Fittings',
      tabName: 'Select Fittings',
      description: 'Please use the below drop downs to select the end style and size you need.',
    },
    step3: {
      title: 'Overall Assembly Length',
      tabName: 'Assembly Length',
      description: 'Please enter the required finished assembly length in inches or feet. See below images for measurement guide.',
    },
    step4: {
      title: 'Angle of Rotation',
      tabName: 'Angle of Rotation',
      description: `Please enter the angle of rotation between fitting&nbsp;1 and fitting&nbsp;2 using the image below as 
a&nbsp;guide.<br/> Fitting 1 will be 0 degrees.`,
    },
    step5: {
      title: 'Accessories/Options',
      tabName: 'Accessories',
      description: 'Please select any optional accessories from the list of available.',
    },
    step6: {
      title: 'Confirm Your Assembly',
      tabName: 'Confirm Assembly',
      // eslint-disable-next-line max-len
      description: 'Please verify component selections, specifications, and assembly length for your application. Custom assemblies are non-returnable and non-refundable. To modify, click the item below.',
    },
    pdf: {
      title: 'Your Assembly',
      // eslint-disable-next-line max-len
      tos: 'Please verify component selections, specifications, and assembly length for your application. Custom assemblies are non-returnable and non-refundable.',
      hoseSelection: 'Hose Selection',
      fitting1: 'Fitting 1',
      fitting2: 'Fitting 2',
      overallAssemblyLength: 'Overall Assembly Length',
      angleOfRotation: 'Angle of Rotation',
      customerPartNumber: 'Customer Part Number',
      assemblyQty: 'Assembly Qty',
      testingTitle: 'Performed to industry standards',
      testingDescription: 'Pressure Test and Certify Assembly',
      accessories: 'Accessories',
    },
    nav: {
      profile: 'Profile',
      product: 'Product Type',
    },
    btn: {
      showFittingDetails: 'Show fitting details',
      showHoseDetails: 'Show hose details',
      hideFittingDetails: 'Hide fitting details',
      hideHoseDetails: 'Hide hose details',
      complete: 'Generate PDF Summary',
      emailBtn: 'Send By Email',
    },
    fitting: {
      // eslint-disable-next-line max-len
      hint: 'Pressure ratings of some hydraulic threads may be less than that of the hose. The lower of the two will determine the pressure rating of the system. For more information on pressure ratings, visit the technical information section of our website.',
    },
    assemblyGroup: {
      toast: {
        added: 'Success! Assembly added to group',
        updated: 'Assembly updated',
        removed: 'Assembly removed',
        limitReached: 'Maximum {{limit}} assemblies allowed. Generate PDF to start a new group.',
        pdfError: 'PDF generation failed. Please try again.',
        pdfSuccess: 'PDF downloaded successfully',
      },
      viewButton: 'View My Assemblies',
      addButton: 'Add to Group',
      addBuildAnother: 'Add & Build Another',
      addGroupBuildAnother: 'Save & Build Another',
      startOver: 'Start Over',
      // Shown before the footer's Start Over drops the finished order and
      // begins a new session.
      startOverConfirm: {
        title: 'Start over?',
        message: 'This begins a new session. The assemblies from this one stay in the PDF you just generated.',
        note: 'There is no undo.',
        confirmBtn: 'Yes, start over',
      },
      updateButton: 'Update Assembly',
      title: 'My Assemblies',
      itemCount: '({{count}} Items)',
      totalAssemblies: 'Total Assemblies: {{count}}',
      totalPartsQty: 'Total Parts Qty: {{count}}',
      generatePdf: 'Generate PDF',
      email: 'Email',
      back: 'Back',
      noNotes: '[No notes]',
      assemblyOf: 'Assembly {{current}} of {{total}}',
      generating: 'Generating...',
      completeAssembly: 'Finish & Generate PDF',
      openPdf: 'Open PDF',
    },
  },
} as const;

export type Type = typeof customization;
export type Texts = DeepKeys<typeof customization>;

export default customization;
